import { buildSortOrderPayload } from '../lib/sortOrderPayload';
import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
// FEATURE (2026-10-09, Jaime): touch-first drag-and-drop via dnd-kit —
// pointer-based (mouse + touch), drag initiated ONLY from the grip handles.
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent, type DragStartEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
// 2026-09-24 (Jaime ruling): create-your-unit nudge for home-less users.
import { Plus, Pencil, Check, Trash2, X, Loader2, AlertCircle, ShoppingCart, Share2, ChevronDown, RefreshCw, GripVertical } from 'lucide-react';
import { UnitPrompt } from '../components/HomeStateBanners';
import { homeApi } from '../services/homeApi';
import { pageErrorFromApi } from '../services/planLimitModal';
import QuantityStepper from '../components/QuantityStepper';
import { useAuth } from '../contexts/AuthContext';
// FEATURE (2026-10-08, Jaime): per-list + button uses i18n for the aria-label.
import { useTranslation } from 'react-i18next';
import { useActiveHomeUnitId } from '../hooks/useActiveHomeUnitId';
import { useEntitlements } from '../hooks/useEntitlements';
import { homeModuleEntitled } from '../utils/homeEntitlementGate';
// Beacon lane (2026-10-01): silent empty-200 diagnostic (TH1 class).
import { beaconEmptySuccess } from '../lib/homeBeacons';

interface ShoppingItem {
  id: string;
  name: string;
  quantity: string;
  category: string;
  assignedTo: string;
  urgency: 'low' | 'medium' | 'high';
  purchased: boolean;
  status: 'pending' | 'completed';
}

// FEATURE (2026-08-31, Jaime): product categories are now exactly these four.
// Legacy food-style ids (fruits/dairy/meat/pantry/cleaning/other/general) are
// mapped to the closest new category for DISPLAY only — no DB data is rewritten.
const categories = [
  { id: 'mercado', name: 'Mercado', icon: '🛒' },
  { id: 'limpieza', name: 'Limpieza', icon: '🧼' },
  { id: 'mantenimiento', name: 'Mantenimiento', icon: '🔧' },
  { id: 'otros', name: 'Otros', icon: '📦' },
];

const CATEGORY_IDS = new Set(categories.map(c => c.id));
const LEGACY_CATEGORY_MAP: Record<string, string> = {
  fruits: 'mercado',
  dairy: 'mercado',
  meat: 'mercado',
  pantry: 'mercado',
  cleaning: 'limpieza',
  maintenance: 'mantenimiento',
  other: 'otros',
  general: 'otros',
};
// FEATURE (2026-09-23, Jaime): list items render alphabetically A-Z within each
// group (category sections and the purchased section). Case-insensitive,
// Spanish-locale sort so accents order naturally (e.g. Áloe after Abrecereales).
const compareByName = (a: { name: string }, b: { name: string }): number =>
  a.name.localeCompare(b.name, 'es', { sensitivity: 'base' });

const normalizeCategory = (raw?: string | null): string => {
  const key = (raw ?? '').trim().toLowerCase();
  // FEATURE (2026-10-09, Jaime): items created in a custom list carry
  // category = 'custom:' + list id. Keep those values intact so the
  // custom-list sections can group them; unknown custom ids are collapsed
  // defensively at render time (sections only render for lists returned by
  // GET /home/shopping/custom-lists — raw 'custom:<id>' strings never show).
  if (key.startsWith('custom:')) return key;
  if (LEGACY_CATEGORY_MAP[key]) return LEGACY_CATEGORY_MAP[key];
  return key && CATEGORY_IDS.has(key) ? key : 'otros';
};

const urgencyColors: Record<string, string> = {
  low: 'text-gray-400',
  medium: 'text-yellow-500 dark:text-yellow-400',
  high: 'text-red-600 dark:text-red-400',
};

const urgencyLabels: Record<string, string> = {
  low: 'Baja',
  medium: 'Media',
  high: 'Alta',
};

// FEATURE (2026-10-09, Jaime — mobile drag-and-drop): render-prop wrapper so
// useSortable can wrap existing JSX without threading props through new
// components. Module-level so re-renders never remount the sortable subtree.
type SortableRowProps = {
  id: string;
  data: { type: 'list' | 'item'; listKey?: string };
  children: (p: {
    setNodeRef: (el: HTMLElement | null) => void;
    setActivatorNodeRef: (el: HTMLElement | null) => void;
    handleProps: Record<string, unknown>;
    transform: { x: number; y: number } | null;
    isDragging: boolean;
  }) => ReactNode;
};
function SortableRow({ id, data, children }: SortableRowProps) {
  const { setNodeRef, setActivatorNodeRef, attributes, listeners, transform, isDragging } = useSortable({ id, data });
  return (
    <>
      {children({
        setNodeRef,
        setActivatorNodeRef,
        handleProps: { ...attributes, ...listeners },
        transform: transform ?? null,
        isDragging,
      })}
    </>
  );
}
// Vertical drag transform (avoids the extra @dnd-kit/utilities dependency).
const dragTransformStyle = (t: { x: number; y: number } | null) =>
  t ? { transform: `translate3d(${Math.round(t.x)}px, ${Math.round(t.y)}px, 0)` } : undefined;

const emptyForm = {
  name: '',
  quantity: '1',
  category: 'otros',
  assignedTo: '',
  urgency: 'medium' as ShoppingItem['urgency'],
};

export default function HomeShopping() {
  const { user } = useAuth();
  const { t } = useTranslation('common');
  // FIX (2026-08-31, Jaime bug): explicit active-unit scoping for every
  // home-module request (owned unit OR condo membership unit, e.g. TH1).
  const activeUnitId = useActiveHomeUnitId();
  // fix-173c: entitlement gate — usuariosOnly / home-* stripped payload means
  // NO /api/home/shopping call at all (no 403 banner; neutral empty state).
  const { entitlement, loading: entitlementLoading } = useEntitlements();
  const [items, setItems] = useState<ShoppingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);

  // FEATURE (2026-08-31, Jaime): inline item edit + row quantity stepper.
  // PUT /api/home/shopping/:id accepts itemName/quantity/category/assignedTo/
  // urgency/status (DAL allowlist in database/home.js Shopping.update).
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ name: '', quantity: '1', category: 'otros', urgency: 'medium' as ShoppingItem['urgency'] });
  const [editSaving, setEditSaving] = useState(false);

  // FEATURE (2026-08-31): "don't retype products" typeahead
  interface Suggestion {
    itemName?: string;
    item_name?: string;
    name?: string;
    category?: string;
    lastQuantity?: string | number;
    last_quantity?: string | number;
    lastUrgency?: string;
    last_urgency?: string;
    timesAdded?: number;
    times_added?: number;
  }
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // FEATURE (2026-08-31, Jaime): "Compartir" is now export-as-text. The
  // pending items are formatted as "Producto x CANT" lines and handed to the
  // OS share sheet (navigator.share → WhatsApp/SMS/email on mobile), with a
  // clipboard fallback + toast on desktop.
  const [shareToast, setShareToast] = useState<string | null>(null);

  // FEATURE (2026-10-08, Jaime): share with list selection. Compartir opens a
  // small multi-select picker (Mercado/Mantenimiento/Limpieza/Otros); only the
  // pending items of the selected lists go into the share text. Same share
  // mechanism as before (navigator.share → clipboard fallback).
  // FEATURE (2026-10-09, Jaime): user-created custom shopping lists alongside
  // the 4 fixed categories. Fetched from GET /api/home/shopping/custom-lists
  // (unit-scoped). Items in a custom list carry category = 'custom:' + list id.
  interface CustomList {
    id: string;
    name: string;
    icon?: string | null;
    color?: string | null;
  }
  const [customLists, setCustomLists] = useState<CustomList[]>([]);
  const [showNewListModal, setShowNewListModal] = useState(false);
  const [newListForm, setNewListForm] = useState({ name: '', icon: '📋', color: '' });
  const [newListError, setNewListError] = useState<string | null>(null);
  const [newListSaving, setNewListSaving] = useState(false);
  const [deleteListTarget, setDeleteListTarget] = useState<CustomList | null>(null);
  const [listToast, setListToast] = useState<string | null>(null);

  const showListToast = (msg: string) => {
    setListToast(msg);
    setTimeout(() => setListToast(null), 3000);
  };

  // Select options shared by the create + inline edit forms: fixed categories
  // first, then custom lists (value 'custom:' + id) so a product added from a
  // custom list's "+" lands in that list.
  const categoryOptions = [
    ...categories,
    ...customLists.map(l => ({ id: `custom:${l.id}`, name: l.name, icon: l.icon || '📋' })),
  ];

  const [showSharePicker, setShowSharePicker] = useState(false);
  const [shareSelected, setShareSelected] = useState<string[]>(categories.map(c => c.id));
  const [shareError, setShareError] = useState<string | null>(null);
  // FEATURE (2026-10-09): the share picker includes custom lists too. Values
  // are category values ('custom:' + id) so the pending-item filter below
  // works unchanged. Pending counts computed for every shareable list.
  const shareableLists = [
    ...categories,
    ...customLists.map(l => ({ id: `custom:${l.id}`, name: l.name, icon: l.icon || '📋' })),
  ];
  const shareCounts: Record<string, number> = {};
  shareableLists.forEach(c => {
    shareCounts[c.id] = items.filter(i => i.category === c.id && !i.purchased).length;
  });
  // FIX (2026-08-31): backend rows are snake_case (item_name, assigned_to,
  // status, purchased_at). Normalize once so the UI always has name/purchased.
  const normalizeItem = (raw: any): ShoppingItem => ({
    id: raw.id,
    name: raw.itemName ?? raw.item_name ?? raw.name ?? '',
    quantity: raw.quantity ?? '',
    category: normalizeCategory(raw.category),
    assignedTo: raw.assignedTo ?? raw.assigned_to ?? '',
    urgency: (raw.urgency ?? 'medium') as ShoppingItem['urgency'],
    purchased: raw.status === 'completed' || raw.purchased === true || !!raw.purchased_at,
    status: raw.status ?? (raw.purchased || raw.purchased_at ? 'completed' : 'pending'),
  });

  const fetchItems = useCallback(async () => {
    // Jaime 2026-09-30: never fire a unitId-dependent request without a
    // resolved unit — show the unit-picker prompt state instead.
    if (!activeUnitId) { setItems([]); setError(null); setLoading(false); return; }
    // fix-1004 (BUG-A, QA bugfix-cycle-1004-0400): entitlement fail-open edge.
    // The 173c gate must only skip the fetch when a payload EXISTS and denies
    // home-shopping. While the payload is still loading we wait (usuariosOnly
    // must not hit /api/home/*); if it never arrives (fetch error / no cached
    // value) we fail OPEN per homeEntitlementGate.ts's documented rule — the
    // backend remains the authoritative 403 guard, so behavior degrades to
    // pre-173c, never worse. The previous `!entitlementReady` check failed
    // closed forever whenever the payload was unavailable, so entitled users
    // never fetched the list (no GET on load or after a 201 create).
    if (!entitlement && entitlementLoading) { return; }
    if (entitlement && !homeModuleEntitled(entitlement, 'home-shopping')) { setItems([]); setError(null); setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await homeApi.shopping.list(activeUnitId);
      const payload = res.data?.data ?? res.data;
      const items = payload?.items || payload?.data?.items || (Array.isArray(payload) ? payload : []);
      const fetched = Array.isArray(items) ? items.map(normalizeItem) : [];
      // Beacon: 200 with an empty list → session-deduped empty_success report.
      beaconEmptySuccess({ route: '/shopping', unitId: activeUnitId, rowCount: fetched.length });
      // Keep local state for rows inside the 3s checked-feedback window so a
      // refetch can't yank them into "Comprado" early.
      setItems(prev => {
        const localById = new Map(prev.map(i => [i.id, i] as const));
        return fetched.map(f => {
          const local = localById.get(f.id);
          return local && justCheckedRef.current.has(f.id) ? local : f;
        });
      });
    } catch (err: any) {
      setError(pageErrorFromApi(err, 'Error al cargar lista de compras'));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [activeUnitId, entitlement, entitlementLoading]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // FEATURE (2026-10-09, Jaime): fetch the unit's custom shopping lists
  // alongside the items. Same unit + entitlement gating as the items fetch.
  // Custom lists are additive: a failure here must never block the 4 fixed
  // sections (empty array → only fixed categories render).
  const fetchCustomLists = useCallback(async () => {
    if (!activeUnitId) { setCustomLists([]); return; }
    if (entitlement && !homeModuleEntitled(entitlement, 'home-shopping')) { setCustomLists([]); return; }
    try {
      const res = await homeApi.shopping.customLists.list(activeUnitId);
      const payload = res.data?.data ?? res.data;
      const lists = payload?.lists ?? payload?.data?.lists ?? (Array.isArray(payload) ? payload : []);
      const safe = (Array.isArray(lists) ? lists : []).filter(
        (l: any) => l && typeof l.id === 'string' && typeof l.name === 'string'
      );
      setCustomLists(safe);
    } catch {
      setCustomLists([]);
    }
  }, [activeUnitId, entitlement]);

  useEffect(() => { fetchCustomLists(); }, [fetchCustomLists]);

  // FEATURE (2026-10-09): create a custom list. POST /home/shopping/custom-lists.
  // 400 errors (reserved fixed name / 60+ chars / 12-list cap) are surfaced
  // inline in the modal — never silently swallowed.
  const submitNewList = async () => {
    if (!newListForm.name.trim() || newListSaving) return;
    setNewListSaving(true);
    setNewListError(null);
    try {
      await homeApi.shopping.customLists.create(
        {
          name: newListForm.name.trim(),
          ...(newListForm.icon ? { icon: newListForm.icon } : {}),
          ...(newListForm.color ? { color: newListForm.color } : {}),
        },
        activeUnitId
      );
      setShowNewListModal(false);
      setNewListForm({ name: '', icon: '📋', color: '' });
      await fetchCustomLists();
    } catch (err: any) {
      const status = err?.response?.status;
      const msg: string = err?.response?.data?.error ?? '';
      if (status === 400 && /reserved/i.test(msg)) {
        setNewListError(t('homeShopping.newListReserved'));
      } else if (status === 400 && /limit/i.test(msg)) {
        setNewListError(t('homeShopping.newListLimit'));
      } else if (status === 400 && /60|length|1\.\.60/i.test(msg)) {
        setNewListError(t('homeShopping.newListNameInvalid'));
      } else {
        setNewListError(msg || t('homeShopping.newListError'));
      }
    } finally {
      setNewListSaving(false);
    }
  };

  // FEATURE (2026-10-09): delete an empty custom list after explicit confirm.
  // NO-DATA-LOSS: backend returns 409 {error, itemCount} when the list still
  // has items — surface that clearly ("vacíala primero") instead of failing.
  const confirmDeleteList = async () => {
    if (!deleteListTarget) return;
    const target = deleteListTarget;
    setDeleteListTarget(null);
    try {
      await homeApi.shopping.customLists.remove(target.id, activeUnitId);
      showListToast(t('homeShopping.listDeleted'));
      await Promise.all([fetchCustomLists(), fetchItems()]);
    } catch (err: any) {
      const status = err?.response?.status;
      const itemCount = err?.response?.data?.itemCount;
      if (status === 409) {
        // fix-F2 (2026-10-09): the confirm dialog closes before this state
        // lands, so the page-level error banner alone was easy to miss in QA.
        // Show the FE-translated message (es-MX, with itemCount) on BOTH the
        // page banner and the list toast near the lists. Never surface the
        // API's English error text.
        const hasItemsMsg = t('homeShopping.listDeleteHasItems', { count: itemCount ?? 0 });
        setError(hasItemsMsg);
        showListToast(hasItemsMsg);
      } else {
        setError(t('homeShopping.listDeleteError'));
      }
    }
  };

  // Ids with an in-flight optimistic mutation (status toggle / qty stepper).
  // The poller must not clobber these rows mid-update (same reconcile idea as
  // togglePurchased: never revert an optimistic local state).
  const mutatingIds = useRef<Set<string>>(new Set());

  // FEATURE (2026-09-18, Jaime): when a product is tapped as bought, it shows
  // CHECKED immediately but stays in the pending list for ~3s (visual
  // feedback) before moving to "Comprado". The API call fires immediately;
  // only the LIST TRANSITION is delayed. Unchecking during the 3s window
  // cancels the pending move and reverts/persists status back to 'pending'.
  const [justChecked, setJustChecked] = useState<Set<string>>(new Set());
  const justCheckedRef = useRef<Set<string>>(new Set());
  const moveTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const addJustChecked = (id: string) => {
    justCheckedRef.current.add(id);
    setJustChecked(new Set(justCheckedRef.current));
  };
  const removeJustChecked = (id: string) => {
    if (!justCheckedRef.current.has(id)) return;
    justCheckedRef.current.delete(id);
    setJustChecked(new Set(justCheckedRef.current));
  };

  // Unmount cleanup: no stray timers firing after the screen is gone.
  useEffect(() => () => {
    moveTimersRef.current.forEach(t => clearTimeout(t));
    moveTimersRef.current.clear();
  }, []);

  // POLLING v1 (2026-08-31, Jaime): pragmatic ~6s refresh while this screen is
  // mounted so items added/edited/completed by other users of the same unit
  // appear without a manual reload. NOTE: this is polling v1 — a websocket
  // layer can replace it later without changing the merge semantics below.
  // Rules:
  //   - tick only when document.visibilityState === 'visible' (paused on
  //     hidden tabs; an immediate catch-up poll fires on visibilitychange)
  //   - skipped while an inline edit / add form is open so we
  //     never clobber someone mid-edit
  //   - rows with an in-flight optimistic mutation keep their local values
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (editingId || showForm) return;
      if (mutatingIds.current.size > 0) return;
      // fix-173c: entitlement gate — no poll when home-shopping is not entitled.
      if (!entitlement || !homeModuleEntitled(entitlement, 'home-shopping')) return;
      try {
        const res = await homeApi.shopping.list(activeUnitId);
        if (cancelled) return;
        const payload = res.data?.data ?? res.data;
        const list = payload?.items || payload?.data?.items || (Array.isArray(payload) ? payload : []);
        if (!Array.isArray(list)) return;
        const fetched = list.map(normalizeItem);
        setItems(prev => {
          const localById = new Map(prev.map(i => [i.id, i] as const));
          return fetched.map(f => {
            const local = localById.get(f.id);
            return local && mutatingIds.current.has(f.id) ? local : f;
          });
        });
        setError(null);
      } catch {
        // Silent: a transient poll error must not replace the visible list.
      }
    };
    const t = setInterval(poll, 6000);
    const onVis = () => { if (document.visibilityState === 'visible') void poll(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
    // FIX (2026-08-31, Jaime bug): include activeUnitId in deps. Without it the
    // interval captured the unitId from the first mount, so after switching
    // units the ~6s poll kept refetching the PREVIOUS unit's list and
    // overwriting the (correct) unit-scoped fetch. Re-creating the interval on
    // unitId change gives every tick a fresh, explicit unitId.
  }, [editingId, showForm, activeUnitId, entitlement]);

  // Debounced suggestion fetch (>=1 char, hide when no matches)
  useEffect(() => {
    const q = form.name.trim();
    if (!q) { setSuggestions([]); setShowSuggestions(false); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      // fix-173c: no suggestion API call when home-shopping is not entitled.
      if (!entitlement || !homeModuleEntitled(entitlement, 'home-shopping')) return;
      try {
        // Explicit unitId: requireUnit auto-resolve breaks multi-unit owners
        const res = await homeApi.shopping.suggestions(q, activeUnitId);
        const payload = res.data?.data ?? res.data;
        const list = payload?.suggestions ?? payload?.items ?? (Array.isArray(payload) ? payload : []);
        const norm = (Array.isArray(list) ? list : []).map((sg: any) => ({
          itemName: sg.itemName ?? sg.item_name ?? sg.name ?? '',
          category: normalizeCategory(sg.category),
          lastQuantity: sg.lastQuantity ?? sg.last_quantity ?? undefined,
          lastUrgency: sg.lastUrgency ?? sg.last_urgency ?? undefined,
          timesAdded: sg.timesAdded ?? sg.times_added ?? 0,
        })).filter((sg: any) => sg.itemName);
        if (cancelled) return;
        setSuggestions(norm);
        setShowSuggestions(norm.length > 0);
      } catch {
        if (!cancelled) { setSuggestions([]); setShowSuggestions(false); }
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.name, activeUnitId, entitlement]);

  const applySuggestion = (sg: Suggestion) => {
    setForm(prev => ({
      ...prev,
      name: sg.itemName ?? '',
      category: sg.category ?? prev.category,
      quantity: sg.lastQuantity != null ? String(sg.lastQuantity) : prev.quantity,
      urgency: (sg.lastUrgency as ShoppingItem['urgency']) ?? prev.urgency,
    }));
    setShowSuggestions(false);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return;
    try {
      await homeApi.shopping.create({ ...form, itemName: form.name }, activeUnitId);
      setShowForm(false);
      setForm(emptyForm);
      fetchItems();
    } catch (err: any) {
      setError(pageErrorFromApi(err, 'Error al agregar item'));
    }
  };

  // FIX (2026-08-31): status contract is 'pending' | 'completed'. Optimistic
  // update on the status field, then reconcile from the response item; revert
  // on error so the check circle never silently reverts.
  const togglePurchased = async (item: ShoppingItem) => {
    // Cancel any pending 3s move for this row (single timer per id).
    const pendingTimer = moveTimersRef.current.get(item.id);
    if (pendingTimer) {
      clearTimeout(pendingTimer);
      moveTimersRef.current.delete(item.id);
    }

    // Uncheck DURING the 3s feedback window: cancel the move, revert locally
    // and persist status back to 'pending' on the server.
    if (justCheckedRef.current.has(item.id)) {
      removeJustChecked(item.id);
      mutatingIds.current.add(item.id);
      setItems(prev => prev.map(i => (i.id === item.id ? { ...i, status: 'pending', purchased: false } : i)));
      try {
        await homeApi.shopping.update(item.id, { status: 'pending' }, activeUnitId);
      } catch (err: any) {
        fetchItems();
        setError(pageErrorFromApi(err, 'Error al actualizar item'));
      } finally {
        mutatingIds.current.delete(item.id);
      }
      return;
    }

    // Plain uncheck from the "Comprado" list: unchanged behavior.
    if (item.purchased) {
      mutatingIds.current.add(item.id);
      setItems(prev => prev.map(i => (i.id === item.id
        ? { ...i, status: 'pending', purchased: false }
        : i)));
      try {
        const res = await homeApi.shopping.update(item.id, { status: 'pending' }, activeUnitId);
        const updated = res.data?.data?.item ?? res.data?.item;
        if (updated) {
          const normalized = normalizeItem(updated);
          setItems(prev => prev.map(i => (i.id === item.id ? normalized : i)));
        } else {
          fetchItems();
        }
      } catch (err: any) {
        // Revert optimistic change
        setItems(prev => prev.map(i => (i.id === item.id ? item : i)));
        setError(pageErrorFromApi(err, 'Error al actualizar item'));
      } finally {
        mutatingIds.current.delete(item.id);
      }
      return;
    }

    // Check: mark CHECKED visually right away, fire the API immediately, and
    // only move the row to "Comprado" after ~3s. While the row is in the
    // feedback window it keeps purchased=false so the pending list/computed
    // counts are unaffected; the server is already 'completed'.
    mutatingIds.current.add(item.id);
    addJustChecked(item.id);
    try {
      await homeApi.shopping.update(item.id, { status: 'completed' }, activeUnitId);
    } catch (err: any) {
      removeJustChecked(item.id);
      mutatingIds.current.delete(item.id);
      setError(pageErrorFromApi(err, 'Error al actualizar item'));
      return;
    }
    const t = setTimeout(() => {
      moveTimersRef.current.delete(item.id);
      mutatingIds.current.delete(item.id);
      removeJustChecked(item.id);
      setItems(prev => prev.map(i => (i.id === item.id
        ? { ...i, status: 'completed', purchased: true }
        : i)));
    }, 3000);
    moveTimersRef.current.set(item.id, t);
  };

  // FEATURE (2026-08-31, Jaime): open inline edit with the item's current values.
  const openEdit = (item: ShoppingItem) => {
    setEditingId(item.id);
    setEditForm({
      name: item.name,
      quantity: item.quantity || '1',
      category: item.category,
      urgency: item.urgency,
    });
  };

  const saveEdit = async () => {
    if (!editingId || !editForm.name.trim()) return;
    setEditSaving(true);
    try {
      await homeApi.shopping.update(editingId, {
        itemName: editForm.name.trim(),
        quantity: editForm.quantity,
        category: editForm.category,
        urgency: editForm.urgency,
      }, activeUnitId);
      setEditingId(null);
      fetchItems();
    } catch (err: any) {
      setError(pageErrorFromApi(err, 'Error al actualizar item'));
    } finally {
      setEditSaving(false);
    }
  };

  // FEATURE (2026-08-31, Jaime): row +/- stepper. Optimistic quantity update,
  // then reconcile the quantity from the response item — same pattern as
  // togglePurchased. Minus is disabled at quantity 1 (items never auto-delete
  // at 0; deletion stays an explicit action).
  const changeQty = async (item: ShoppingItem, delta: number) => {
    const current = parseInt(item.quantity, 10);
    const base = Number.isFinite(current) && current > 0 ? current : 1;
    const next = Math.max(1, base + delta);
    if (next === base) return;
    mutatingIds.current.add(item.id);
    setItems(prev => prev.map(i => (i.id === item.id ? { ...i, quantity: String(next) } : i)));
    try {
      const res = await homeApi.shopping.update(item.id, { quantity: String(next) }, activeUnitId);
      const updated = res.data?.data?.item ?? res.data?.item;
      if (updated) {
        const normalized = normalizeItem(updated);
        setItems(prev => prev.map(i => (i.id === item.id ? { ...i, quantity: normalized.quantity } : i)));
      }
    } catch (err: any) {
      setItems(prev => prev.map(i => (i.id === item.id ? { ...i, quantity: item.quantity } : i)));
      setError(pageErrorFromApi(err, 'Error al actualizar cantidad'));
    } finally {
      mutatingIds.current.delete(item.id);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      // FIX (2026-09-28, ticket #216): pass the active unitId — same source as
      // the GET calls — otherwise requireUnit 400s "unitId is required" for
      // multi-unit owners and the item never gets removed.
      await homeApi.shopping.delete(id, activeUnitId);
      fetchItems();
    } catch (err: any) {
      setError(pageErrorFromApi(err, 'Error al eliminar item'));
    }
  };

  const shareList = async () => {
    // FEATURE (2026-10-08, Jaime): share only the selected lists' pending items.
    const pending = items.filter(i => !i.purchased && shareSelected.includes(i.category));
    if (pending.length === 0) {
      setShareToast(t('homeShopping.shareEmpty'));
      setTimeout(() => setShareToast(null), 2500);
      return;
    }
    const text = pending
      .map(i => `${i.name} x ${parseInt(i.quantity, 10) || 1}`)
      .join('\n');
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: t('homeShopping.shareTitle'), text });
        return;
      } catch {
        // user cancelled or share failed — fall through to clipboard
      }
    }
    try {
      await navigator.clipboard.writeText(text);
      setShareToast(t('homeShopping.shareCopied'));
    } catch {
      setShareToast(t('homeShopping.shareCopyFail'));
    }
    setTimeout(() => setShareToast(null), 3000);
  };
  const openSharePicker = () => {
    // FEATURE (2026-10-08, Jaime): preselect lists that actually have pending items.
    const listsWithPending = shareableLists.filter(c => items.some(i => i.category === c.id && !i.purchased));
    setShareSelected(listsWithPending.map(c => c.id));
    setShareError(null);
    setShowSharePicker(true);
  };

  const toggleShareList = (id: string) =>
    setShareSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  const cancelShare = () => {
    setShowSharePicker(false);
    setShareError(null);
  };
  const confirmShare = async () => {
    if (shareSelected.length === 0) {
      setShareError(t('homeShopping.shareRequired'));
      return;
    }
    setShowSharePicker(false);
    setShareError(null);
    await shareList();
  };

  // FEATURE (2026-10-08, Jaime): open the existing add-product form with a
  // specific list (category) pre-selected. Triggered by the small "+" button
  // beside each list title (Mercado/Mantenimiento/Limpieza/Otros). The generic
  // "Agregar Item" button keeps opening the form with the default category.
  const openAddFor = (categoryId: string) => {
    setForm({ ...emptyForm, category: categoryId });
    setSuggestions([]);
    setShowSuggestions(false);
    setShowForm(true);
    if (typeof document !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const uncheckedCount = items.filter(i => !i.purchased).length;
  const checkedCount = items.filter(i => i.purchased).length;

  // FEATURE (2026-09-03, Jaime): collapsible category sections. Default is
  // expanded (no behavior change on load); each category toggles independently.
  // Collapsible-section state is persisted in localStorage under a single key
  // so collapse choices survive reloads. Guarded parse falls back to defaults.
  const COLLAPSED_SECTIONS_KEY = 'home-shopping-collapsed-sections';
  const loadCollapsedSections = (): { categories: Record<string, boolean>; purchased: boolean } => {
    if (typeof window === 'undefined') return { categories: {}, purchased: false };
    try {
      const raw = window.localStorage.getItem(COLLAPSED_SECTIONS_KEY);
      if (!raw) return { categories: {}, purchased: false };
      const parsed = JSON.parse(raw);
      return {
        categories: parsed && typeof parsed === 'object' && typeof parsed.categories === 'object' && parsed.categories !== null ? parsed.categories : {},
        purchased: parsed?.purchased === true,
      };
    } catch {
      return { categories: {}, purchased: false };
    }
  };
  const writeCollapsedSections = (next: { categories: Record<string, boolean>; purchased: boolean }) => {
    try {
      window.localStorage.setItem(COLLAPSED_SECTIONS_KEY, JSON.stringify(next));
    } catch {
      // storage unavailable (quota/private mode) — collapse state just won't persist
    }
  };
  const initialCollapsed = loadCollapsedSections();
  const [collapsedCategories, setCollapsedCategories] = useState<Record<string, boolean>>(initialCollapsed.categories);
  const [purchasedCollapsed, setPurchasedCollapsed] = useState(initialCollapsed.purchased);
  const toggleCategoryCollapsed = (id: string) =>
    setCollapsedCategories(prev => {
      const next = { ...prev, [id]: !prev[id] };
      writeCollapsedSections({ categories: next, purchased: purchasedCollapsed });
      return next;
    });

  // FEATURE (2026-10-09, Jaime): drag-and-drop reordering of the LIST
  // sections and the PRODUCTS within each list. Pinned BE contract:
  // GET/PUT /home/shopping/sort-order →
  // {success, data:{listOrder:string[]|null, itemOrder:Record<string,string[]>|null}}.
  // List keys = fixed category ids + 'custom:<id>' (one shared order space).
  // On load: silently ignore failures — defaults (alphabetical items,
  // fixed+custom list order) render exactly as before. Drops reorder locally
  // (optimistic) and PUT in the background; a PUT failure shows a small toast
  // and KEEPS the local order (non-blocking).
  const [listOrder, setListOrder] = useState<string[] | null>(null);
  const [itemOrder, setItemOrder] = useState<Record<string, string[]> | null>(null);
  const [sortToast, setSortToast] = useState<string | null>(null);
  const showSortToast = (msg: string) => {
    setSortToast(msg);
    setTimeout(() => setSortToast(null), 3000);
  };

  useEffect(() => {
    let cancelled = false;
    if (!activeUnitId) { setListOrder(null); setItemOrder(null); return; }
    (async () => {
      try {
        const res = await homeApi.shopping.sortOrder.get(activeUnitId);
        if (cancelled) return;
        const payload = res.data?.data ?? res.data;
        const lo = Array.isArray(payload?.listOrder)
          ? payload.listOrder.filter((k: unknown): k is string => typeof k === 'string')
          : null;
        let io: Record<string, string[]> | null = null;
        const ioRaw = payload?.itemOrder;
        if (ioRaw && typeof ioRaw === 'object' && !Array.isArray(ioRaw)) {
          io = {};
          for (const [k, v] of Object.entries(ioRaw)) {
            if (Array.isArray(v)) io[k] = (v as unknown[]).filter((x): x is string => typeof x === 'string');
          }
        }
        setListOrder(lo);
        setItemOrder(io);
      } catch {
        // Silent: sort-order is a presentation preference; defaults apply.
      }
    })();
    return () => { cancelled = true; };
  }, [activeUnitId]);

  // blocker-B1 fix (2026-10-09): BE isValidSortOrder (shopping.routes.js)
  // requires BOTH listOrder (array) and itemOrder (object) on every PUT —
  // partial payloads always 400'd, so drag-and-drop never persisted. Both
  // calls now send the full persisted state; the not-yet-saved side uses its
  // null-safe default ({} for itemOrder, current visual section order for
  // listOrder). buildSortOrderPayload asserts the contract before the PUT.
  const persistListOrder = async (order: string[]) => {
    setListOrder(order);
    try {
      await homeApi.shopping.sortOrder.put(
        buildSortOrderPayload(order, itemOrder ?? {}),
        activeUnitId
      );
    } catch {
      showSortToast(t('homeShopping.reorderSaveFail'));
    }
  };

  const persistItemOrder = async (listKey: string, ids: string[], currentListOrder: string[]) => {
    setItemOrder(prev => ({ ...(prev ?? {}), [listKey]: ids }));
    try {
      await homeApi.shopping.sortOrder.put(
        buildSortOrderPayload(currentListOrder, { ...(itemOrder ?? {}), [listKey]: ids }),
        activeUnitId
      );
    } catch {
      showSortToast(t('homeShopping.reorderSaveFail'));
    }
  };

  // Items of one list sorted by the saved itemOrder; ids missing from the
  // saved order (new items) are appended after the ordered ones.
  const sortItemsFor = (listKey: string, list: ShoppingItem[]) => {
    const ord = itemOrder?.[listKey];
    if (!ord || ord.length === 0) return [...list].sort(compareByName);
    const idx = new Map(ord.map((id, i) => [id, i] as const));
    return list
      .map((it, i) => ({ it, rank: idx.has(it.id) ? (idx.get(it.id) as number) : ord.length + i }))
      .sort((a, b) => a.rank - b.rank)
      .map(x => x.it);
  };

  // FEATURE (2026-10-09, Jaime — mobile drag-and-drop): dnd-kit pointer-based
  // sorting (mouse + touch). Drag starts ONLY from the grip handles (distance
  // activation constraint), so page scroll and every other control keep
  // working as before. Persistence contract unchanged: optimistic local
  // reorder + PUT /home/shopping/sort-order via buildSortOrderPayload (BOTH
  // listOrder + itemOrder) + failure toast (non-blocking).
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );
  const [activeDrag, setActiveDrag] = useState<{ type: 'list' | 'item'; listKey?: string } | null>(null);

  const onDragStart = (e: DragStartEvent) => {
    const d = e.active.data.current as { type?: string; listKey?: string } | undefined;
    if (d?.type === 'list' || d?.type === 'item') {
      setActiveDrag({ type: d.type, listKey: d.listKey });
    }
  };
  const onDragCancel = () => setActiveDrag(null);

  const onDragEnd = (e: DragEndEvent) => {
    const a = e.active.data.current as { type?: string; listKey?: string } | undefined;
    const o = e.over?.data.current as { type?: string; listKey?: string } | undefined;
    setActiveDrag(null);
    if (!e.over || !a?.type) return;
    if (a.type === 'list') {
      // List lane: drop target may be a list section OR an item row inside
      // one — map item drops to their owning list (one shared order space).
      const targetKey = o?.type === 'list'
        ? String(e.over.id)
        : o?.type === 'item' ? (o.listKey ?? null) : null;
      if (!targetKey || targetKey === e.active.id) return;
      const ids = orderedSections.map(sec => sec.id);
      const from = ids.indexOf(String(e.active.id));
      const to = ids.indexOf(targetKey);
      if (from < 0 || to < 0) return;
      void persistListOrder(arrayMove(ids, from, to));
      return;
    }
    if (a.type === 'item' && o?.type === 'item' && a.listKey === o.listKey && e.active.id !== e.over.id) {
      // Item lane: same-list only (unchanged contract).
      const listKey = a.listKey as string;
      const ids = sortItemsFor(listKey, items.filter(i => i.category === listKey && !i.purchased)).map(i => i.id);
      const from = ids.indexOf(String(e.active.id));
      const to = ids.indexOf(String(e.over!.id));
      if (from < 0 || to < 0) return;
      void persistItemOrder(listKey, arrayMove(ids, from, to), listOrder ?? orderedSections.map(sec => sec.id));
    }
  };

  const itemsByCategory = categories.map(cat => ({
    ...cat,
    items: sortItemsFor(cat.id, items.filter(i => i.category === cat.id && !i.purchased)),
  }));

  // FEATURE (2026-10-09, Jaime): custom list sections (fixed ones first).
  // Section ids are the item category values ('custom:' + list id) so the
  // collapse state and share filter reuse the same key space. Icon/color fall
  // back to 📋 when the list has none.
  const customSections = customLists.map(l => ({
    id: `custom:${l.id}`,
    raw: l,
    name: l.name,
    icon: l.icon || '📋',
    color: l.color || undefined,
    items: sortItemsFor(`custom:${l.id}`, items.filter(i => i.category === `custom:${l.id}` && !i.purchased)),
  }));

  // fix-F1 (2026-10-09): custom lists are a resident-only surface. Non-resident
  // Propietarios (usuariosOnly) pass the page render but cannot create lists
  // (API 403s), so hide the "Nueva lista" tile and any custom sections using
  // the SAME home-module gate the items fetch uses (fix-173c pattern).
  const canUseCustomLists = !entitlement || homeModuleEntitled(entitlement, 'home-shopping');

  // FEATURE (2026-10-09, Jaime): fixed + custom lists share ONE order space.
  // listOrder ranks the sections; keys missing from the saved order (new
  // lists) are appended in their default order (fixed first, then custom).
  const sections = [
    ...itemsByCategory.map(s => ({ ...s, isCustom: false, raw: null, color: undefined })),
    ...customSections.map(s => ({ ...s, isCustom: true })),
  ];
  const listOrderIdx = new Map((listOrder ?? []).map((k, i) => [k, i] as const));
  const orderedSections = sections
    .map((sec, i) => ({ sec, rank: listOrderIdx.has(sec.id) ? (listOrderIdx.get(sec.id) as number) : sections.length + i }))
    .sort((a, b) => a.rank - b.rank)
    .map(x => x.sec);

  return (
    <div className="space-y-6">
     
      {/* Header Stats */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 rounded-xl">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-blue-100 text-sm">Por comprar</p>
            <p className="text-4xl font-bold text-white mt-1">{uncheckedCount}</p>
          </div>
          <div className="text-right">
            <p className="text-blue-100 text-sm">Comprado</p>
            <p className="text-2xl font-bold text-white mt-1">{checkedCount}</p>
          </div>
        </div>
      </div>

      {!activeUnitId && !loading && <UnitPrompt />}

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-500/50 text-red-700 dark:text-red-300 p-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button onClick={() => { setError(null); void fetchItems(); }} className="ml-auto flex items-center gap-1 text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"><RefreshCw className="w-4 h-4" />Reintentar</button>
          <button onClick={() => setError(null)} className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Add + Share Buttons */}
      <div className="flex gap-3">
        <button
          onClick={() => { setForm(emptyForm); setSuggestions([]); setShowSuggestions(false); setShowForm(true); }}
          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Agregar Item
        </button>
        <button
          onClick={openSharePicker}
          aria-haspopup="dialog"
          className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2"
        >
          <Share2 className="w-5 h-5" />
          Compartir
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)] space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-[var(--v-text-primary)] dark:text-white font-semibold">Nuevo Item</h3>
            <button onClick={() => setShowForm(false)} className="text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          <div className="relative">
            <input
              type="text"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              onKeyDown={e => { if (e.key === 'Escape') setShowSuggestions(false); }}
              placeholder="¿Qué necesitas comprar?"
              className="w-full bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-4 py-3 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
              autoComplete="off"
            />
            {showForm && showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-gray-100 dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg shadow-lg overflow-hidden">
                {suggestions.map((sg, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onMouseDown={e => { e.preventDefault(); applySuggestion(sg); }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-900 dark:text-white text-sm"
                  >
                    <span>{categories.find(c => c.id === (sg.category ?? 'other'))?.icon ?? '📦'}</span>
                    <span className="flex-1 truncate">{sg.itemName}</span>
                    {!!sg.lastQuantity && <span className="text-gray-500 dark:text-slate-400 text-xs">×{sg.lastQuantity}</span>}
                    {!!sg.timesAdded && <span className="text-gray-400 dark:text-slate-500 text-xs">({sg.timesAdded}×)</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {/* FEATURE (2026-08-31, Jaime): shared +/- stepper in create form */}
            <div className="flex items-center bg-gray-100 dark:bg-slate-700 rounded-lg border border-gray-300 dark:border-slate-600 px-2">
              <QuantityStepper
                value={parseInt(form.quantity, 10) || 1}
                onChange={n => setForm({ ...form, quantity: String(n) })}
              />
            </div>
            {/* FEATURE (2026-10-09, Jaime): the selector gains the custom
                lists (value 'custom:' + id) so a product added from a custom
                list's "+" quick-add lands in that list. */}
            <select
              value={form.category}
              onChange={e => setForm({ ...form, category: e.target.value })}
              aria-label={t('homeShopping.categoryAria')}
              className="bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
            >
              {categoryOptions.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
              ))}
            </select>
            <input
              type="text"
              value={form.assignedTo}
              onChange={e => setForm({ ...form, assignedTo: e.target.value })}
              placeholder="Asignar a..."
              className="bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
            />
            <select
              value={form.urgency}
              onChange={e => setForm({ ...form, urgency: e.target.value as ShoppingItem['urgency'] })}
              className="bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
            >
              <option value="low">Urgencia Baja</option>
              <option value="medium">Urgencia Media</option>
              <option value="high">Urgencia Alta</option>
            </select>
          </div>
          <button
            onClick={handleSubmit}
            className="w-full bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg font-medium"
          >
            Agregar a la Lista
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12 text-gray-500 dark:text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Cargando lista...
        </div>
      )}

      {/* Items by Category */}
      {!loading && !error && activeUnitId && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={onDragCancel}>
        <SortableContext items={orderedSections.map(sec => sec.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-6">
          {itemsByCategory.length === 0 && checkedCount === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart className="w-16 h-16 text-gray-300 dark:text-slate-600 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-slate-400">La lista está vacía</p>
              <p className="text-gray-400 dark:text-slate-500 text-sm mt-1">¡Agrega items para comprar!</p>
              <button
                type="button"
                onClick={() => { setForm(emptyForm); setSuggestions([]); setShowSuggestions(false); setShowForm(true); }}
                className="mt-4 rounded-xl bg-[#36CCD8] px-4 py-2.5 text-sm font-semibold text-white"
              >
                Agregar el primer artículo
              </button>
            </div>
          ) : (
            orderedSections.map(cat => (
              <SortableRow key={cat.id} id={cat.id} data={{ type: 'list' }}>
              {({ setNodeRef, setActivatorNodeRef, handleProps, transform, isDragging }) => (
              <div
                ref={setNodeRef}
                style={dragTransformStyle(transform)}
                className={`relative ${isDragging ? 'opacity-50 z-20' : ''}`}
              >
              <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-surface-3)] overflow-hidden">
                {/* FEATURE (2026-10-09, Jaime — mobile): grip handle at the
                    LEFT of the emoji+name header (per screenshot feedback).
                    Drag starts ONLY from this grip; the collapse chevron and
                    +/delete buttons stay at the right and keep working. Fixed
                    + custom lists share one order space; a drop reorders
                    optimistically and PUTs /home/shopping/sort-order. */}
                <div className="w-full bg-gray-100 dark:bg-slate-700/50 px-2 py-2 flex items-center gap-1">
                  <button
                    type="button"
                    ref={setActivatorNodeRef}
                    {...handleProps}
                    data-testid={`shopping-grip-${cat.id}`}
                    aria-label={t('homeShopping.reorderListAria', { list: cat.name })}
                    title={t('homeShopping.reorderListAria', { list: cat.name })}
                    className="flex-shrink-0 w-10 h-10 flex items-center justify-center cursor-grab active:cursor-grabbing touch-none text-gray-400 dark:text-slate-500"
                  >
                    <GripVertical className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleCategoryCollapsed(cat.id)}
                    aria-expanded={!collapsedCategories[cat.id]}
                    aria-controls={`shopping-cat-${cat.id}`}
                    className="flex-1 min-w-0 hover:bg-gray-200 dark:hover:bg-slate-700 -my-1 py-1 flex items-center gap-2 text-left transition-colors rounded"
                  >
                    <span className="text-xl" style={cat.color ? { color: cat.color } : undefined}>{cat.icon}</span>
                    <span className="font-semibold text-[var(--v-text-primary)] dark:text-white">{cat.name}</span>
                    <span className="text-gray-500 dark:text-slate-400 text-sm ml-auto">{cat.items.length}</span>
                    <ChevronDown
                      className={`w-4 h-4 text-gray-400 dark:text-slate-400 flex-shrink-0 transition-transform ${collapsedCategories[cat.id] ? '-rotate-90' : ''}`}
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() => openAddFor(cat.id)}
                    aria-label={t('homeShopping.addToList', { list: cat.name })}
                    title={t('homeShopping.addToList', { list: cat.name })}
                    data-testid={`shopping-add-${cat.id}`}
                    className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                  {cat.isCustom && (
                    <button
                      type="button"
                      onClick={() => setDeleteListTarget(cat.raw)}
                      aria-label={t('homeShopping.listDeleteAria', { list: cat.name })}
                      title={t('homeShopping.listDeleteAria', { list: cat.name })}
                      data-testid={`shopping-list-delete-${cat.id}`}
                      className="flex-shrink-0 w-7 h-7 rounded-full text-gray-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center justify-center transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div id={`shopping-cat-${cat.id}`} className={collapsedCategories[cat.id] ? 'hidden' : 'p-4 space-y-2'}>
                  {cat.items.length === 0 && (
                    <p className="text-gray-400 dark:text-slate-500 text-sm">{t('homeShopping.sectionEmpty')}</p>
                  )}
                  <SortableContext items={cat.items.map(i => i.id)} strategy={verticalListSortingStrategy}>
                  {cat.items.map((item) => (
                    <SortableRow key={item.id} id={item.id} data={{ type: 'item', listKey: cat.id }}>
                    {({ setNodeRef, setActivatorNodeRef, handleProps, transform, isDragging }) => (
                    <div
                      ref={setNodeRef}
                      style={dragTransformStyle(transform)}
                      className={`relative flex items-center gap-1 p-2 hover:bg-gray-200 dark:hover:bg-slate-700/50 rounded-lg group ${isDragging ? 'opacity-50 z-20' : ''}`}
                    >
                      {/* FEATURE (2026-10-09, Jaime — mobile): grip at the LEFT
                          of the item row (before the checkbox circle). Drag
                          starts ONLY here, so tapping/scrolling elsewhere on
                          the page behaves normally. */}
                      <button
                        type="button"
                        ref={setActivatorNodeRef}
                        {...handleProps}
                        data-testid={`shopping-item-grip-${item.id}`}
                        aria-label={t('homeShopping.reorderItemAria', { name: item.name })}
                        title={t('homeShopping.reorderItemAria', { name: item.name })}
                        className="flex-shrink-0 w-10 h-10 flex items-center justify-center cursor-grab active:cursor-grabbing touch-none text-gray-400 dark:text-slate-500"
                      >
                        <GripVertical className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => togglePurchased(item)}
                        aria-label={justChecked.has(item.id) ? 'Marcado como comprado' : 'Marcar como comprado'}
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${justChecked.has(item.id) ? 'bg-green-500 border-green-500' : 'border-gray-300 dark:border-slate-500 hover:border-green-500'}`}
                      >
                        {justChecked.has(item.id) && <Check className="w-4 h-4 text-white" />}
                      </button>
                      {editingId === item.id ? (
                        /* FEATURE (2026-08-31, Jaime): inline edit form */
                        <div className="flex-1 min-w-0 grid grid-cols-2 gap-2 py-1">
                          <input
                            type="text"
                            value={editForm.name}
                            onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                            placeholder="Nombre"
                            className="col-span-2 bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-3 py-1.5 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none text-sm"
                            autoComplete="off"
                          />
                          {/* FEATURE (2026-08-31, Jaime): shared +/- stepper in edit form */}
                          <div className="flex items-center bg-gray-100 dark:bg-slate-700 rounded-lg border border-gray-300 dark:border-slate-600 px-1.5">
                            <QuantityStepper
                              size="sm"
                              value={parseInt(editForm.quantity, 10) || 1}
                              onChange={n => setEditForm({ ...editForm, quantity: String(n) })}
                            />
                          </div>
                          <select
                            value={editForm.category}
                            onChange={e => setEditForm({ ...editForm, category: e.target.value })}
                            aria-label={t('homeShopping.categoryAria')}
                            className="bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-3 py-1.5 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none text-sm"
                          >
                            {categoryOptions.map(cat => (
                              <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                            ))}
                          </select>
                          <select
                            value={editForm.urgency}
                            onChange={e => setEditForm({ ...editForm, urgency: e.target.value as ShoppingItem['urgency'] })}
                            className="bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-3 py-1.5 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none text-sm"
                          >
                            <option value="low">Urgencia Baja</option>
                            <option value="medium">Urgencia Media</option>
                            <option value="high">Urgencia Alta</option>
                          </select>
                          <div className="flex gap-2">
                            <button
                              onClick={saveEdit}
                              disabled={editSaving || !editForm.name.trim()}
                              className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-green-600/40 text-white py-1.5 rounded-lg text-sm font-medium flex items-center justify-center gap-1"
                            >
                              {editSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                              Guardar
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-slate-300 py-1.5 px-3 rounded-lg text-sm"
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex-1 min-w-0">
                            <p className="text-[var(--v-text-primary)] dark:text-white font-medium">{item.name}</p>
                            <p className="text-gray-500 dark:text-slate-400 text-sm">
                              {item.quantity}
                              {item.assignedTo && ` • ${item.assignedTo}`}
                              {item.urgency && (
                                <span className={`ml-2 ${urgencyColors[item.urgency]}`}>⚠ {urgencyLabels[item.urgency]}</span>
                              )}
                            </p>
                          </div>
                          {/* FEATURE (2026-08-31, Jaime): +/- quantity stepper (shared component) */}
                          <QuantityStepper
                            size="sm"
                            value={parseInt(item.quantity, 10) || 1}
                            onChange={n => changeQty(item, n - (parseInt(item.quantity, 10) || 1))}
                          />
                          <button
                            onClick={() => openEdit(item)}
                            className="text-gray-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 p-1 flex-shrink-0 opacity-100 pointer-events-auto sm:opacity-0 sm:pointer-events-none sm:group-hover:opacity-100 sm:group-hover:pointer-events-auto transition-opacity"
                            aria-label="Editar"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="text-gray-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-600 p-1 flex-shrink-0 opacity-100 pointer-events-auto sm:opacity-0 sm:pointer-events-none sm:group-hover:opacity-100 sm:group-hover:pointer-events-auto transition-opacity"
                        aria-label="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    )}
                    </SortableRow>
                  ))}
                  </SortableContext>
                </div>
              </div>
              </div>
              )}
              </SortableRow>
            ))
            )}

            {/* FEATURE (2026-10-09, Jaime): "Nueva lista" affordance — a
                tile below the list sections opens the create-list modal.
                fix-F1: hidden for non-resident users (usuariosOnly) whose
                create attempt would 403 — same gate as the items fetch. */}
            {canUseCustomLists && (
            <button
              type="button"
              onClick={() => { setNewListForm({ name: '', icon: '📋', color: '' }); setNewListError(null); setShowNewListModal(true); }}
              data-testid="shopping-new-list"
              className="w-full border-2 border-dashed border-gray-300 dark:border-slate-600 hover:border-blue-500 dark:hover:border-blue-400 rounded-xl py-3 flex items-center justify-center gap-2 text-gray-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            >
              <Plus className="w-5 h-5" />
              {t('homeShopping.newListButton')}
            </button>
            )}
        </div>
        </SortableContext>
        </DndContext>
      )}

      {/* Purchased Items */}
      {!loading && checkedCount > 0 && (
        <div className="bg-[var(--v-surface-1)]/50 rounded-xl border border-[var(--v-surface-3)] p-4">
          <button
            type="button"
            onClick={() =>
              setPurchasedCollapsed(v => {
                const next = !v;
                writeCollapsedSections({ categories: collapsedCategories, purchased: next });
                return next;
              })
            }
            aria-expanded={!purchasedCollapsed}
            aria-controls="shopping-purchased"
            className="w-full text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300 font-medium flex items-center gap-2 mb-4 text-left"
          >
            <Check className="w-4 h-4" />
            Comprado ({checkedCount})
            <ChevronDown
              className={`w-4 h-4 ml-auto flex-shrink-0 transition-transform ${purchasedCollapsed ? '-rotate-90' : ''}`}
            />
          </button>
          <div id="shopping-purchased" className={purchasedCollapsed ? 'hidden' : 'space-y-2 opacity-60'}>
            {items.filter(i => i.purchased).sort(compareByName).map((item) => (
              <div key={item.id} className="flex items-center gap-3 p-2 group">
                <button
                  onClick={() => togglePurchased(item)}
                  className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0"
                >
                  <Check className="w-4 h-4 text-white" />
                </button>
                <span className="text-gray-500 dark:text-slate-400 line-through flex-1">{item.name}</span>
                <button
                  onClick={() => handleDelete(item.id)}
                  className="text-gray-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-600 dark:text-red-400 p-1 flex-shrink-0 opacity-100 pointer-events-auto sm:opacity-0 sm:pointer-events-none sm:group-hover:opacity-100 sm:group-hover:pointer-events-auto"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* FEATURE (2026-10-08, Jaime): share list-selection modal */}
      {showSharePicker && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={t('homeShopping.shareTitle')}
          onClick={cancelShare}
        >
          <div
            className="bg-[var(--v-surface-1)] dark:bg-slate-800 w-full max-w-sm rounded-xl border border-[var(--v-surface-3)] p-4 space-y-3"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-[var(--v-text-primary)] dark:text-white font-semibold">{t('homeShopping.shareTitle')}</h3>
              <button onClick={cancelShare} aria-label={t('homeShopping.shareCancel')} className="text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-gray-500 dark:text-slate-400 text-sm">{t('homeShopping.shareHint')}</p>
            <div className="space-y-2">
              {shareableLists.map(cat => {
                const checked = shareSelected.includes(cat.id);
                const count = shareCounts[cat.id] || 0;
                return (
                  <label
                    key={cat.id}
                    className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${checked ? 'border-purple-500 bg-purple-500/10' : 'border-gray-300 dark:border-slate-600 hover:border-purple-400'}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleShareList(cat.id)}
                      aria-label={t('homeShopping.shareAria', { list: cat.name })}
                      className="w-4 h-4 accent-purple-600"
                    />
                    <span aria-hidden="true">{cat.icon}</span>
                    <span className="text-[var(--v-text-primary)] dark:text-white font-medium flex-1">{cat.name}</span>
                    <span className="text-gray-500 dark:text-slate-400 text-sm">
                      {count > 0 ? t('homeShopping.sharePendingCount', { count }) : '—'}
                    </span>
                  </label>
                );
              })}
            </div>
            {shareError && (
              <p className="text-red-600 dark:text-red-400 text-sm" role="alert">{shareError}</p>
            )}
            <div className="flex gap-2 pt-1">
              <button
                onClick={confirmShare}
                disabled={shareSelected.length === 0}
                className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-600/40 text-white py-2.5 rounded-lg font-medium"
              >
                {t('homeShopping.shareAction')}
              </button>
              <button
                onClick={cancelShare}
                className="bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-slate-300 py-2.5 px-4 rounded-lg text-sm"
              >
                {t('homeShopping.shareCancel')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* FEATURE (2026-10-09, Jaime): create custom list modal */}
      {showNewListModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={t('homeShopping.newListTitle')}
          onClick={() => setShowNewListModal(false)}
        >
          <div
            className="bg-[var(--v-surface-1)] dark:bg-slate-800 w-full max-w-sm rounded-xl border border-[var(--v-surface-3)] p-4 space-y-3"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-[var(--v-text-primary)] dark:text-white font-semibold">{t('homeShopping.newListTitle')}</h3>
              <button
                onClick={() => setShowNewListModal(false)}
                aria-label={t('homeShopping.newListCancel')}
                className="text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div>
              <label htmlFor="new-list-name" className="text-sm text-gray-500 dark:text-slate-400">{t('homeShopping.newListNameLabel')}</label>
              <input
                id="new-list-name"
                type="text"
                value={newListForm.name}
                onChange={e => setNewListForm({ ...newListForm, name: e.target.value.slice(0, 60) })}
                maxLength={60}
                placeholder={t('homeShopping.newListNamePlaceholder')}
                autoComplete="off"
                data-testid="shopping-new-list-name"
                className="w-full mt-1 bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-4 py-2.5 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
              />
              {/* live char counter (max 60) */}
              <p className={`text-xs mt-1 text-right ${newListForm.name.length >= 60 ? 'text-red-600 dark:text-red-400' : 'text-gray-400 dark:text-slate-500'}`}>
                {newListForm.name.length}/60
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-slate-400">{t('homeShopping.newListIconLabel')}</p>
              <div className="flex flex-wrap gap-2 mt-1" role="radiogroup" aria-label={t('homeShopping.newListIconLabel')}>
                {['📋', '🛒', '🧺', '💊', '🎉', '🏠', '🚗', '🎁'].map(emoji => (
                  <button
                    key={emoji}
                    type="button"
                    role="radio"
                    aria-checked={newListForm.icon === emoji}
                    onClick={() => setNewListForm({ ...newListForm, icon: emoji })}
                    className={`w-9 h-9 rounded-lg text-xl flex items-center justify-center border transition-colors ${newListForm.icon === emoji ? 'border-blue-500 bg-blue-500/10' : 'border-gray-300 dark:border-slate-600 hover:border-blue-400'}`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-slate-400">{t('homeShopping.newListColorLabel')}</p>
              <div className="flex flex-wrap gap-2 mt-1" role="radiogroup" aria-label={t('homeShopping.newListColorLabel')}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={newListForm.color === ''}
                  onClick={() => setNewListForm({ ...newListForm, color: '' })}
                  className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs ${newListForm.color === '' ? 'border-blue-500' : 'border-gray-300 dark:border-slate-600'}`}
                >
                  —
                </button>
                {['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'].map(color => (
                  <button
                    key={color}
                    type="button"
                    role="radio"
                    aria-checked={newListForm.color === color}
                    aria-label={color}
                    onClick={() => setNewListForm({ ...newListForm, color })}
                    style={{ backgroundColor: color }}
                    className={`w-7 h-7 rounded-full border-2 ${newListForm.color === color ? 'border-gray-900 dark:border-white' : 'border-transparent'}`}
                  />
                ))}
              </div>
            </div>
            {newListError && (
              <p className="text-red-600 dark:text-red-400 text-sm" role="alert">{newListError}</p>
            )}
            <div className="flex gap-2 pt-1">
              <button
                onClick={submitNewList}
                disabled={newListSaving || !newListForm.name.trim()}
                data-testid="shopping-new-list-submit"
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/40 text-white py-2.5 rounded-lg font-medium flex items-center justify-center gap-2"
              >
                {newListSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('homeShopping.newListCreate')}
              </button>
              <button
                onClick={() => setShowNewListModal(false)}
                className="bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-slate-300 py-2.5 px-4 rounded-lg text-sm"
              >
                {t('homeShopping.newListCancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FEATURE (2026-10-09, Jaime): delete custom list confirm dialog */}
      {deleteListTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={t('homeShopping.listDeleteAria', { list: deleteListTarget.name })}
          onClick={() => setDeleteListTarget(null)}
        >
          <div
            className="bg-[var(--v-surface-1)] dark:bg-slate-800 w-full max-w-sm rounded-xl border border-[var(--v-surface-3)] p-4 space-y-3"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-[var(--v-text-primary)] dark:text-white font-semibold">
              {t('homeShopping.listDeleteConfirm', { name: deleteListTarget.name })}
            </h3>
            <p className="text-gray-500 dark:text-slate-400 text-sm">{t('homeShopping.listDeleteHint')}</p>
            <div className="flex gap-2 pt-1">
              <button
                onClick={confirmDeleteList}
                data-testid="shopping-list-delete-confirm"
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-lg font-medium"
              >
                {t('homeShopping.listDeleteAction')}
              </button>
              <button
                onClick={() => setDeleteListTarget(null)}
                className="bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-slate-300 py-2.5 px-4 rounded-lg text-sm"
              >
                {t('homeShopping.newListCancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FEATURE (2026-10-09, Jaime): custom list action toast (created / deleted) */}
      {listToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-4 py-2.5 rounded-lg shadow-lg text-sm z-50" role="status">
          {listToast}
        </div>
      )}
      {/* FEATURE (2026-08-31, Jaime): clipboard fallback toast */}
      {shareToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-green-600 text-white px-4 py-2.5 rounded-lg shadow-lg text-sm z-50">
          {shareToast}
        </div>
      )}
    </div>
  );
}
